/**
 * The help content, in one place, because the screens and the help page must
 * never disagree.
 *
 * Why it is shaped like this. HROps is used a handful of times a month, by
 * people who are not the ones who built it, to do something that cannot be
 * undone by clicking again. That combination rules out the two usual answers:
 *
 * - **A tour** teaches at the wrong moment. Nobody remembers step four of an
 *   onboarding tour three weeks later, standing in the form, wondering whether
 *   Send As is the thing they want.
 * - **Tooltips everywhere** teach nothing. A `?` on every label is noise, and
 *   noise is skipped, including on the two or three labels that genuinely
 *   carry consequences.
 *
 * So: one narrative page that reads end to end (`/help`), and inline help ONLY
 * on the terms where being wrong costs something — each one opening the same
 * definition the page uses, and linking into it. `TERMS` is that shared set.
 *
 * Writing rules for anything added here:
 *
 * 1. Say what the system does, not what the user should feel about it.
 * 2. Name the consequence, including the irreversible ones. "Removing the
 *    licence starts a 30-day fuse" is the sentence somebody needs at 17:00 on
 *    a Friday.
 * 3. Never state how the backend is configured. Dry-run, scheduling and the
 *    like are ours to manage; HR cannot act on them, and an earlier version
 *    that explained them here confused the people it was written for. The
 *    Operations view (/admin) carries that.
 */

export type HelpTermId =
  | "upn"
  | "delegate"
  | "send-as"
  | "shared-mailbox"
  | "phase-1"
  | "phase-2"
  | "revert-window"
  | "risk-flag"
  | "action-log"
  | "partial"
  | "reinstate"
  | "immediately"
  | "access-list"
  | "workforce-change"
  | "future-hire-id";

export type HelpTerm = {
  /** The heading, and the label the inline trigger falls back to. */
  title: string;
  /** Two or three sentences. Long enough to be useful, short enough to read standing up. */
  body: string;
  /** The consequence, when getting it wrong costs something. Rendered as a warning. */
  caution?: string;
};

export const TERMS: Record<HelpTermId, HelpTerm> = {
  upn: {
    title: "UPN",
    body: "The name the person signs in with. Not their email address: sign-in names end in @noricangroup.com, mail addresses in @norican.com.",
    caution:
      "Type a surname and pick the person from the list — that is what stops the wrong Anna being offboarded.",
  },
  delegate: {
    title: "Mailbox delegate",
    body: "The colleague who takes over the mailbox. Full Access: they can read everything in it, including what arrived before today. It appears in their Outlook by itself, usually within a few hours; restarting Outlook speeds it up.",
    caution:
      "Name the person who genuinely needs the mail. This is real access to somebody's correspondence, recorded against your name.",
  },
  "send-as": {
    title: "Send As",
    body: "Off by default. Without it the delegate reads the mailbox but sends under their own name; with it, their mail appears to come from the leaver.",
    caution:
      "Only for a customer-facing address that has to keep answering. The recipient cannot tell the difference.",
  },
  "shared-mailbox": {
    title: "Shared mailbox conversion",
    body: "In phase 2 the mailbox becomes shared: the content stays, the delegate keeps access, and the E5 licence is released. Over 50 GB or on litigation hold the E5 stays, because a shared mailbox is free only up to 50 GB. An empty online archive is removed.",
    caution:
      "Convert first, release the licence second — enforced in code. The wrong order starts a 30-day fuse on the leaver's mail.",
  },
  "phase-1": {
    title: "Phase 1",
    body: "Everything at the effective time, and everything that can be undone: account disabled in Entra and on-prem AD, sessions revoked, devices disabled, mailbox handed over.",
  },
  "phase-2": {
    title: "Phase 2",
    body: "The irreversible half: the mailbox becomes shared and the E5 licence is released (unless the mailbox is over 50 GB or on litigation hold), after the revert window has passed.",
    caution: "Nothing in phase 2 can be undone from HROps.",
  },
  "revert-window": {
    title: "Revert window",
    body: "Fourteen days between phase 1 and phase 2, so a wrongful offboarding can be undone before anything irreversible happens.",
  },
  "risk-flag": {
    title: "Risk flag",
    body: "A time-boxed note that HR is watching a situation. Low and medium change nothing for the person; high blocks uploads to file-sharing services, private webmail and AI assistants such as ChatGPT, blocks copying files to USB, and caps mail attachments to outside recipients at 10 MB.",
    caution:
      "Every flag expires, thirty days by default. A flag that never ends is a permanent suspicion.",
  },
  "access-list": {
    title: "Access list",
    body: "A group whose membership HR keeps up to date. Adding somebody gives them what the group grants, removing them takes it away, and nothing else changes for the person. Each change is applied within a few minutes and logged against the person who asked.",
    caution:
      "Only the groups set up for HROps appear. There is no way to reach any other group from here.",
  },
  "action-log": {
    title: "Action log",
    body: "Every step, with the person who decided and the application that acted. Append-only: a correction is a new row, never an edit.",
    caution:
      "Entra's own audit log only records the service principal. This is the only place the human who asked is named.",
  },
  partial: {
    title: "Partial",
    body: "Some steps worked and at least one failed. The failed ones are retried automatically.",
    caution:
      "Worth chasing: the account can be disabled in the cloud and still live on-prem. Tell SecOps.",
  },
  reinstate: {
    title: "Re-enable the account",
    body: "Puts the person back: the account is enabled again in Entra and in on-prem AD. Only on a record HROps itself ran.",
    caution:
      "Different from Revert, which only marks the record. After phase 2 this does not bring back the licence or un-share the mailbox.",
  },
  immediately: {
    title: "What “immediately” actually means",
    body: "On-prem access stops within seconds. Mail, files and Teams follow within about fifteen minutes; anything else can hold a token for up to an hour.",
    caution:
      "If somebody must be out of everything this minute, call SecOps — this is not the tool.",
  },
  "workforce-change": {
    title: "Workforce change",
    body: "The input form for hires, exits, planned exits, future positions and changes to somebody's supervisor, job, location or legal entity. The responsible HR Business Partner submits it; an approver accepts it or sends it back with a note, and the submitter is told by mail either way.",
    caution:
      "Approval does things: an exit raises the offboarding, an organisational change is written to Active Directory, a new hire is handed to the IT helpdesk for the account. Compensation on a record is visible only to the submitter, the responsible HRBP and the approvers.",
  },
  "future-hire-id": {
    title: "Future Hire ID",
    body: "Assigned by HROps when a future position is submitted (FH-0001, FH-0002, …). When the person is hired, the new-hire form names that ID, so target and actual compensation sit on the same position and nothing is counted twice.",
  },
};

/** One step of the end-to-end story on `/help`. */
export type HelpStep = {
  title: string;
  body: string;
  /** Terms explained inline on the page, so the section stands alone. */
  terms?: HelpTermId[];
};

export const STEPS: HelpStep[] = [
  {
    title: "1. HR raises it",
    body: "Name the leaver, the date and time, and who takes the mailbox. You confirm it before it is saved.",
    terms: ["upn", "delegate", "send-as"],
  },
  {
    title: "2. It waits in the queue",
    body: "Until the effective time. The cheap moment to fix a mistake.",
  },
  {
    title: "3. Phase 1 runs",
    body: "Account disabled in Entra AND on-prem AD, sessions revoked, devices disabled, mailbox handed to the delegate. Both directories, or directory sync simply undoes it.",
    terms: ["phase-1", "immediately"],
  },
  {
    title: "4. Nothing is assumed",
    body: "Directory role holders, protected admins and break-glass accounts are refused, and anything that cannot be checked counts as a refusal.",
  },
  {
    title: "5. Fourteen days to change your mind",
    body: "Revert marks the record; Re-enable the account actually puts them back.",
    terms: ["revert-window", "reinstate"],
  },
  {
    title: "6. Phase 2 closes it",
    body: "Mailbox converted to shared, then the licence released. The point of no return.",
    terms: ["phase-2", "shared-mailbox"],
  },
  {
    title: "7. All of it is on the record",
    body: "Two log rows per step, never edited, never deleted.",
    terms: ["action-log", "partial"],
  },
];

/** Who can do what. Mirrors the app roles on NO-AR-HROps-App. */
export const ROLE_HELP: Array<{ role: string; label: string; can: string }> = [
  {
    role: "HROps.User",
    label: "User",
    can: "Everything HROps does, on any record: raise, revert, cancel, re-enable, flag, retire, and keep the access lists.",
  },
  {
    role: "HROps.Workforce",
    label: "Workforce",
    can: "The Workforce page: report hires, exits, planned exits, future positions and org-data changes; approve them when also on the approver list.",
  },
  {
    role: "HROps.Admin",
    label: "Admin",
    can: "The same, plus the Operations view — what is half-applied, what last failed, what is not configured. No extra functional rights.",
  },
];
